import FeaturesPageTrackedButton from 'components/features-page/FeaturesPageTrackedButton'
import Svg from 'components/UI/SVG'
import { graphql, useStaticQuery } from 'gatsby'
import linuxIcon from 'images/linux-icon.svg'
import macOSIcon from 'images/macos-icon.svg'
import windowsIcon from 'images/windows-icon.svg'
import { Flex, Text } from 'theme-ui'
import getEnglishStrings from 'utils/getEnglishStrings'
import chromeIcon from 'images/chrome-icon.svg'
import firefoxIcon from 'images/firefox-icon.svg'
import edgeIcon from 'images/edge-icon.svg'
import iosIcon from 'images/ios-features.svg'
import androidIcon from 'images/android-features.svg'

export default function FeaturePageDownloadButtons({
  hideSignUp = false,
  path,
}: {
  hideSignUp?: boolean
  path: string
}): JSX.Element {
  const downloadButtonsData = useStaticQuery<Queries.DesktopDowloadButtonsQuery>(graphql`
    query DesktopDowloadButtons {
      directus {
        general_translations {
          strings {
            languages_code {
              code
            }
            sign_up
          }
        }
        desktop_page {
          strings {
            languages_code {
              code
            }
            download_for_macos
            download_for_windows
            download_for_linux
          }
        }
        browser_page {
          strings {
            languages_code {
              code
            }
            download_for_chrome
            download_for_firefox
            download_for_edge
          }
        }
        mobile_page {
          strings {
            languages_code {
              code
            }
            download_for_ios
            download_for_android
          }
        }
      }
    }
  `)

  const generalData = getEnglishStrings(downloadButtonsData.directus?.general_translations?.strings)

  const desktopPageStrings = getEnglishStrings(downloadButtonsData.directus?.desktop_page?.strings)
  const browserPageStrings = getEnglishStrings(downloadButtonsData.directus?.browser_page?.strings)
  const mobilePageStrings = getEnglishStrings(downloadButtonsData.directus?.mobile_page?.strings)

  const buttonCollection = {
    '/features/windows/': {
      icon: windowsIcon,
      text: desktopPageStrings?.download_for_windows ?? '',
      link: '/download?platform=desktop&os=windows',
    },
    '/features/macos/': {
      icon: macOSIcon,
      text: desktopPageStrings?.download_for_macos ?? '',
      link: '/download?platform=desktop&os=macos',
    },
    '/features/linux/': {
      icon: linuxIcon,
      text: desktopPageStrings?.download_for_linux ?? '',
      link: '/download?platform=desktop&os=linux',
    },
    '/features/chrome/': {
      icon: chromeIcon,
      text: browserPageStrings?.download_for_chrome ?? '',
      link: 'https://chromewebstore.google.com/detail/windscribe-free-proxy-and/hnmpcagpplmpfojmgmnngilcnanddlhb?pli=1',
    },
    '/features/firefox/': {
      icon: firefoxIcon,
      text: browserPageStrings?.download_for_firefox ?? '',
      link: 'https://addons.mozilla.org/en-US/firefox/addon/windscribe',
    },
    '/features/edge/': {
      icon: edgeIcon,
      text: browserPageStrings?.download_for_edge ?? '',
      link: 'https://microsoftedge.microsoft.com/addons/detail/windscribe-free-proxy-a/dkkdbpgldnmkhcliffjpajcfdjkcaddf',
    },
    '/features/ios/': {
      icon: iosIcon,
      text: mobilePageStrings?.download_for_ios ?? '',
      link: '/download?platform=mobile&os=ios',
    },
    '/features/android/': {
      icon: androidIcon,
      text: mobilePageStrings?.download_for_android ?? '',
      link: '/download?platform=mobile&os=android',
    },
  }

  return (
    <Flex
      sx={{
        gap: '1.2rem',
        flexWrap: 'wrap',
        justifyContent: 'flex-start',
        a: {
          display: 'flex',
          justifyContent: 'center',
          textAlign: 'center',
          minWidth: ['100%', '100%', 'auto'],
          width: '100%',
        },
      }}
    >
      <FeaturesPageTrackedButton
        url={buttonCollection[path].link}
        dataTestId={`${path}-download-button`}
      >
        <Flex sx={{ justifyContent: 'flex-start', alignItems: 'center', gap: '1.6rem' }}>
          <Svg svg={buttonCollection[path].icon} sx={{ minWidth: '2.4rem', minHeight: '2.4rem' }} />
          <Text>{buttonCollection[path].text}</Text>
        </Flex>
      </FeaturesPageTrackedButton>
      {!hideSignUp && (
        <FeaturesPageTrackedButton variant="buttons.neon" url={`/signup`}>
          {generalData?.sign_up}
        </FeaturesPageTrackedButton>
      )}
    </Flex>
  )
}
